import { API } from 'aws-amplify'
import { StlProviders, TrackingModes } from 'components/Patients/TreatmentTracker/txTrackerConsts'
import { TreatmentTypes } from 'consts/treatmentConsts'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { logError } from 'utils/logUtils'
import { providerOptionsForTrackingMode } from './txTrackerProviderOptions'
import usePracticeConfiguration from './usePracticeConfiguration'

// Mirrors the server's TxTrackerValidationErrors. Keep the two in sync: a comparison
// against a member missing here reads `undefined` and silently never fires.
export const ValidationErrors = {
  MoreThanOneSeries: 'moreThanOneSeries',
  DuplicatedStls: 'duplicatedStls',
  InvalidFiles: 'invalidFiles',
  MissingRetentionArch: 'missingRetentionArch',
  ProviderChangeDetected: 'providerChangeDetected',
  // Not returned by the server. Stands in when the call itself failed, so a check that never
  // ran cannot be read as a check that passed.
  UnexpectedError: 'unexpectedError'
}

export const selectableProviderOptions = ({ options, existingStlsProvider }) => {
  // A patient whose STLs were already uploaded with 'Other' must keep it as the locked
  // current value even when the testing flag no longer offers it for new selections.
  if (
    existingStlsProvider === StlProviders.Other &&
    options &&
    !options.some(option => option.value === existingStlsProvider)
  ) {
    return options.concat([{ label: 'Other', value: StlProviders.Other }])
  }

  // Callers iterate and sort the result, so an unknown treatment type yields an empty
  // list rather than undefined.
  return options || []
}

export default ({
  setIsValidationInProgress = () => {},
  treatmentType,
  trackingMode,
  patientId,
  txTrackerStls
} = {}) => {
  const { ownerAppFeatureFlags } = usePracticeConfiguration()
  const practiceFFs = useSelector(state => state.multiPracticeReducer.practiceFFs)

  const hasBetaProviders =
    ownerAppFeatureFlags?.treatmentTrackerBetaProviders || practiceFFs?.treatmentTrackerBetaProviders

  const hasPartialAligners =
    ownerAppFeatureFlags?.treatmentTrackerPartialAligners || practiceFFs?.treatmentTrackerPartialAligners

  const hasTestingProviders = ownerAppFeatureFlags?.txTrackerOtherProviders || practiceFFs?.txTrackerOtherProviders

  const hasJooylign = ownerAppFeatureFlags?.treatmentTrackerJooylign || practiceFFs?.treatmentTrackerJooylign

  const alignersTestingProviders = useMemo(
    () => [
      { label: 'Aligners - Other: Full plan', value: StlProviders.AlignersOtherFullPlan },
      { label: 'Aligners - Other: Start/End', value: StlProviders.AlignersOtherStartEnd }
    ],
    []
  )

  // 'Other' has no automatic Drive-to-S3 sync (files are processed manually by the internal
  // team), so it must not be offered to practices outside the testing feature flag.
  const bracesTestingProviders = useMemo(
    () => [
      { label: 'Braces - Other', value: StlProviders.BracesOther },
      { label: 'Other', value: StlProviders.Other }
    ],
    []
  )

  const providerOptionsByTreatmentType = useMemo(
    () => ({
      [TreatmentTypes.Aligners]: [
        { label: 'Solventum', value: StlProviders['3M'] },
        { label: 'uLab', value: StlProviders.uLab },
        { label: 'ArchForm', value: StlProviders.ArchForm }
      ]
        .concat(
          hasBetaProviders
            ? [
                { label: 'Spark', value: StlProviders.Spark },
                { label: 'Angel', value: StlProviders.Angel },
                { label: 'Tune', value: StlProviders.Tune }
              ]
            : []
        )
        .concat(hasJooylign ? [{ label: 'Jooylign', value: StlProviders.Jooylign }] : [])
        .concat(hasPartialAligners ? [{ label: 'Align', value: StlProviders.Align }] : [])
        .concat(hasTestingProviders ? alignersTestingProviders : []),
      [TreatmentTypes.Braces]: [
        { label: 'Solventum', value: StlProviders['3M'] },
        { label: 'KLOwen', value: StlProviders.KLOwen }
      ].concat(hasTestingProviders ? bracesTestingProviders : [])
    }),
    [
      alignersTestingProviders,
      bracesTestingProviders,
      hasBetaProviders,
      hasPartialAligners,
      hasTestingProviders,
      hasJooylign
    ]
  )

  const existingStlsProvider = useMemo(() => JSON.parse(txTrackerStls || '{}').provider, [txTrackerStls])

  const providerOptions = useMemo(
    () =>
      selectableProviderOptions({
        options: providerOptionsForTrackingMode({
          providerOptionsByTreatmentType,
          treatmentType,
          isRetentionMode: trackingMode === TrackingModes.Retention
        }),
        existingStlsProvider
      }),
    [providerOptionsByTreatmentType, treatmentType, trackingMode, existingStlsProvider]
  )

  const runTxTrackerValidations = useCallback(
    async ({ provider, step, files, trackingMode }) => {
      setIsValidationInProgress(true)

      let validationsResponse

      try {
        validationsResponse = await API.post('grinServerlessApi', '/treatment-tracker/v1/validation', {
          body: {
            patientId,
            provider,
            step,
            newStls: files || [],
            ...(trackingMode ? { trackingMode } : {})
          }
        })
      } catch (ex) {
        logError('runTxTrackerValidations: server error', { ex, patientId, provider, step, trackingMode })
        setIsValidationInProgress(false)
        return { validationError: ValidationErrors.UnexpectedError }
      }

      setIsValidationInProgress(false)

      return validationsResponse || {}
    },
    [setIsValidationInProgress, patientId]
  )

  return {
    runTxTrackerValidations,
    ValidationErrors,
    setIsValidationInProgress,
    providerOptions
  }
}
